/**
 * buildRecipeIndex
 *
 * Pure function. Takes a course recipe object and returns a flat lookup table
 * (RecipeIndex) used by the lesson engine for all navigation and progress logic.
 *
 * No React. No side effects. Safe to call anytime.
 *
 * Throws immediately if any duplicate IDs are found — this is a recipe authoring
 * error that must be caught before the engine starts.
 */

function buildRecipeIndex(courseRecipe) {
  const courseIds = [];
  const lessonIds = [];
  const stepIds = [];
  const stepItemIds = [];

  const parentOf = {};
  const childrenOf = {};
  const nodeOf = {};
  const isSkippable = {};
  const isNotMandatory = {};
  const legacyIdsOf = {};
  const legacyModeOf = {};

  const allSeenIds = new Set();

  function registerNode(id, parentId, nodeData, legacyIds, legacyMode) {
    if (allSeenIds.has(id)) {
      throw new Error(
        `[buildRecipeIndex] Duplicate ID found in recipe: "${id}". ` +
        `Each course, lesson, step, and stepItem must have a unique id.`
      );
    }
    allSeenIds.add(id);

    parentOf[id] = parentId ?? null;
    nodeOf[id] = nodeData;
    legacyIdsOf[id] = Array.isArray(legacyIds) ? legacyIds : [];
    legacyModeOf[id] = legacyMode || "anyOf";

    if (!childrenOf[id]) childrenOf[id] = [];
    if (parentId && childrenOf[parentId]) {
      childrenOf[parentId].push(id);
    }
  }

  // Walk the recipe tree
  const course = courseRecipe;
  const courseId = course.id;

  childrenOf[courseId] = [];
  registerNode(courseId, null, {
    id: courseId,
    name: course.name,
    description: course.description,
    version: course.version,
  }, course.legacyIds, course.legacyMode);
  courseIds.push(courseId);

  (course.lessons || []).forEach((lesson) => {
    const lessonId = lesson.id;
    childrenOf[lessonId] = [];
    registerNode(lessonId, courseId, lesson, lesson.legacyIds, lesson.legacyMode);
    lessonIds.push(lessonId);

    (lesson.steps || []).forEach((step) => {
      const stepId = step.id;
      childrenOf[stepId] = [];
      registerNode(stepId, lessonId, step, step.legacyIds, step.legacyMode);
      stepIds.push(stepId);

      (step.stepItems || []).forEach((stepItem) => {
        const stepItemId = stepItem.id;
        childrenOf[stepItemId] = [];
        registerNode(stepItemId, stepId, stepItem, stepItem.legacyIds, stepItem.legacyMode);
        stepItemIds.push(stepItemId);

        isSkippable[stepItemId] = !!stepItem.isSkippable;
        isNotMandatory[stepItemId] = !!stepItem.notMandatory;
      });
    });
  });

  return {
    // Ordered flat lists (defines navigation order)
    courseIds,
    lessonIds,
    stepIds,
    stepItemIds,

    // Relationships
    parentOf,   // id → parentId
    childrenOf, // id → [childId, ...]

    // Full recipe data for any node
    nodeOf,

    // stepItem-specific flags
    isSkippable,
    isNotMandatory, // items that don't count toward "lesson has started"

    // Migration support
    legacyIdsOf,  // id → [oldId, ...]
    legacyModeOf, // id → "anyOf" | "allOf"
  };
}


// ---------------------------------------------------------------------------
// Navigation helpers
// ---------------------------------------------------------------------------

/**
 * Given a stepItemId, returns the next stepItem in recipe order.
 * Returns null if this is the last stepItem in the course.
 */
function getNextStepItemId(idx, stepItemId) {
  const pos = idx.stepItemIds.indexOf(stepItemId);
  if (pos === -1 || pos === idx.stepItemIds.length - 1) return null;
  return idx.stepItemIds[pos + 1];
}

/**
 * Given a stepItemId, returns the previous stepItem in recipe order.
 * Returns null if this is the first stepItem in the course.
 */
function getPrevStepItemId(idx, stepItemId) {
  const pos = idx.stepItemIds.indexOf(stepItemId);
  if (pos <= 0) return null;
  return idx.stepItemIds[pos - 1];
}

/**
 * Returns the first stepItem ID of a given step.
 */
function getFirstStepItemId(idx, stepId) {
  return idx.childrenOf[stepId]?.[0] ?? null;
}

/**
 * Returns the first stepItem in the course that is not completed and not skippable.
 * Falls back to the very first stepItem if all are done.
 * Used for cursor repair when stored activeStepItemId no longer exists in recipe.
 */
function getFirstIncompleteStepItemId(idx, progressById) {
  for (const id of idx.stepItemIds) {
    if (!progressById[id]?.completed && !idx.isSkippable[id]) {
      return id;
    }
  }
  // All done (or all skippable) — return last item
  return idx.stepItemIds[idx.stepItemIds.length - 1] ?? null;
}

/**
 * Given a stepItemId and a progressById, determines what level of
 * celebration should be enqueued after marking that stepItem complete.
 *
 * Returns an array of celebration levels in order, e.g.:
 *   []                    — nothing finished (non-last stepItem)
 *   ["step"]              — step finished
 *   ["step", "lesson"]    — step AND lesson finished
 *   ["step", "lesson", "course"] — everything finished
 */
function enqueueCelebrations(idx, stepItemId, progressById) {
  const stepId = idx.parentOf[stepItemId];
  const lessonId = idx.parentOf[stepId];
  const courseId = idx.parentOf[lessonId];

  const stepDone = isNodeCompleted(idx, stepId, progressById);
  const lessonDone = stepDone && isNodeCompleted(idx, lessonId, progressById);
  const courseDone = lessonDone && isNodeCompleted(idx, courseId, progressById);

  const queue = [];
  if (stepDone) queue.push("step");
  if (lessonDone) queue.push("lesson");
  if (courseDone) queue.push("course");
  return queue;
}

/**
 * Checks whether all children of a node are completed.
 * For stepItems, checks the progressById directly.
 * For steps/lessons/courses, checks all descendants recursively.
 */
function isNodeCompleted(idx, nodeId, progressById) {
  const children = idx.childrenOf[nodeId];

  // Leaf node (stepItem) — check progress directly
  if (!children || children.length === 0) {
    return !!progressById[nodeId]?.completed;
  }

  // Non-leaf — all children must be complete
  return children.every((childId) => isNodeCompleted(idx, childId, progressById));
}

/**
 * Returns the access state of a step for the LessonPanel UI.
 *   "completed" — all stepItems in this step are done
 *   "active"    — this is the first incomplete step in the lesson
 *   "locked"    — a preceding step is not yet complete
 */
function getStepAccessState(idx, progressById, stepId) {
  if (isNodeCompleted(idx, stepId, progressById)) return "completed";

  const lessonId = idx.parentOf[stepId];
  const steps = idx.childrenOf[lessonId] || [];

  for (const sId of steps) {
    if (!isNodeCompleted(idx, sId, progressById)) {
      return sId === stepId ? "active" : "locked";
    }
  }

  return "locked";
}

/**
 * If activeStepItemId no longer exists in the recipe (e.g. after a recipe change),
 * repairs it by returning the first incomplete non-skippable stepItem.
 */
function repairActiveStepItemId(idx, progressById, activeStepItemId) {
  if (idx.nodeOf[activeStepItemId]) return activeStepItemId;

  console.warn(
    `[repairActiveStepItemId] "${activeStepItemId}" no longer exists in recipe. ` +
    `Falling back to first incomplete stepItem.`
  );
  return getFirstIncompleteStepItemId(idx, progressById);
}

/**
 * Resolves what stepId the user is currently viewing in LessonPanel.
 * If viewingStepId is explicitly set (user browsing), returns it.
 * Otherwise derives it from activeStepItemId.
 */
function resolveViewingStepId(idx, activeStepItemId, viewingStepId) {
  if (viewingStepId) return viewingStepId;
  return idx.parentOf[activeStepItemId] ?? null;
}


export {
  buildRecipeIndex,
  getNextStepItemId,
  getPrevStepItemId,
  getFirstStepItemId,
  getFirstIncompleteStepItemId,
  enqueueCelebrations,
  isNodeCompleted,
  getStepAccessState,
  repairActiveStepItemId,
  resolveViewingStepId,
};
